// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: c4e59755-6bb1-4a4a-8294-d9c5ab7490a9

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { typeMeta } from './schema';

// ---------------------------------------------------------------------------
// APPLICATION -> DEPLOYMENT -> SERVERS, drawn.
//
// Frank, 2026-08-20, on the mind map's 300-node ceiling: "should we look at a
// different thing, more of icon to line mapping of some sort?"
//
// Yes, and the reason is not rendering speed. The mind map caps at 300 because
// a radial graph of an entire estate is unreadable long before it is slow --
// making it draw 600 nodes faster would produce a faster hairball. The useful
// picture is not the whole estate at once; it is ONE application's stack, which
// is the question people actually arrive with: what runs this, and where.
//
// So this draws a single application. It is never large by construction -- one
// app, its deployments, their servers -- which sidesteps the scale problem
// rather than optimising it.
//
// WHY IT IS A FIXED THREE-TIER LAYOUT AND NOT A GRAPH ENGINE
//
// The relationship being drawn has a known shape: an application owns
// deployments, a deployment owns servers. That is a tree with three levels and
// a fixed direction, so the layout is arithmetic rather than simulation. No
// force iteration, no animation loop, no layout library -- the same choice
// MindMap.js made and for the same reason: a deterministic picture can be
// printed, diffed and trusted, and one that settles differently each render
// cannot.
//
// Lines are orthogonal (drop, across, drop) because that is what an
// architecture diagram looks like to the people receiving it. Diagonals read as
// a graph; right angles read as infrastructure.
//
// THE URL IS ON THE DEPLOYMENT AND THAT IS THE POINT. Frank: "it might have 5
// app servers behind a LB, it still needs the association." Five servers under
// one deployment IS the load balancer, drawn: the address belongs to the
// deployment, the hosts hang beneath it, and the fan-out is the relationship.
// ---------------------------------------------------------------------------

const APP_W = 210;
const APP_H = 46;
const DEP_W = 190;
const DEP_H = 52;
const SRV_W = 132;
const SRV_H = 34;

const COL_GAP = 26;      // between deployment columns
const SRV_GAP = 10;      // between server boxes
const TIER_GAP = 46;     // vertical space for the connector between tiers
const PAD = 16;

// A deployment with 200 servers would draw 200 boxes nobody reads. The cap is
// generous enough that a real Epic PRD fits, and the overflow is STATED rather
// than silently dropped -- an undrawn server that nothing mentions is worse
// than a box saying there are more.
const SERVER_CAP = 10;

// Servers wrap instead of running out sideways forever. Ten in a row made the
// canvas 2,684px wide for a single application, which is a scrollbar pretending
// to be a diagram. Five per row keeps a realistic Epic PRD on one screen.
const SERVERS_PER_ROW = 5;

const line = (x1, y1, x2, y2, key) => (
  <line key={key} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C6D0D9" strokeWidth={1.5} />
);

// One rounded box with a type-coloured spine down its left edge. The spine is
// the "icon": it uses the same colour the TypeBadge uses everywhere else, so a
// server here and a server in a list are recognisably the same thing without
// needing a legend.
function Node({ x, y, w, h, label, sub, color, onClick, dim }) {
  return (
    <g
      transform={`translate(${x},${y})`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <rect width={w} height={h} rx={6} fill={dim ? '#F7F9FB' : '#FFFFFF'}
        stroke="#DCE3EA" strokeWidth={1} />
      <rect width={4} height={h} rx={2} fill={color} />
      <text x={14} y={sub ? 20 : h / 2 + 4} fontSize={12.5} fontWeight={700} fill="#021F4B">
        {label.length > 24 ? `${label.slice(0, 23)}…` : label}
      </text>
      {sub && (
        <text x={14} y={36} fontSize={10.5} fill="#5C6B7A">
          {sub.length > 30 ? `${sub.slice(0, 29)}…` : sub}
        </text>
      )}
    </g>
  );
}

export default function DeploymentDiagram({ appName, appId, nodes, urls, onOpen }) {
  const model = useMemo(() => {
    // Each deployment column is as wide as its own servers need, so a
    // deployment with one server does not reserve room for ten.
    const cols = nodes.map((d) => {
      const shown = d.servers.slice(0, SERVER_CAP);
      const hidden = d.servers.length - shown.length;
      const perRow = Math.min(SERVERS_PER_ROW, Math.max(1, shown.length));
      const rows = Math.ceil(shown.length / SERVERS_PER_ROW);
      const srvRowW = shown.length ? perRow * SRV_W + (perRow - 1) * SRV_GAP : 0;
      return { ...d, shown, hidden, rows, perRow, width: Math.max(DEP_W, srvRowW) };
    });
    const totalW = cols.reduce((s, c) => s + c.width, 0) + COL_GAP * Math.max(0, cols.length - 1);
    const width = Math.max(totalW, APP_W) + PAD * 2;
    const hasServers = cols.some((c) => c.shown.length);
    const maxRows = Math.max(0, ...cols.map((c) => c.rows));
    const serverBlockH = maxRows ? maxRows * SRV_H + (maxRows - 1) * SRV_GAP : 0;
    const height = PAD + APP_H + TIER_GAP + DEP_H
      + (hasServers ? TIER_GAP + serverBlockH : 0) + PAD + 14;

    let cursor = PAD;
    const placed = cols.map((c) => {
      const x = cursor;
      cursor += c.width + COL_GAP;
      // The deployment box is CENTRED over its own server block. Left-aligning
      // it inside a column as wide as ten servers pushed every box to the left
      // of its own fan-out, so the rail from the application ran off to one
      // side and the picture read as lopsided rather than as a hierarchy.
      const depX = x + Math.max(0, (c.width - DEP_W) / 2);
      return { ...c, x, depX, centre: depX + DEP_W / 2 };
    });
    return { placed, width, height, hasServers, totalW, serverBlockH };
  }, [nodes]);

  // Centred over the deployment rail rather than the canvas. Those are the same
  // thing only when every column is the same width, and they are not: a
  // deployment with ten servers is a far wider column than one with none, so
  // centring on the canvas put the application off to one side of its own
  // children.
  const railFrom = model.placed.length ? model.placed[0].centre : 0;
  const railTo = model.placed.length ? model.placed[model.placed.length - 1].centre : 0;
  const railMid = model.placed.length ? (railFrom + railTo) / 2 : model.width / 2;
  const appX = Math.max(PAD, Math.min(railMid - APP_W / 2, model.width - APP_W - PAD));
  const appY = PAD;
  const depY = appY + APP_H + TIER_GAP;
  const srvY = depY + DEP_H + TIER_GAP;
  const appAnchorX = appX + APP_W / 2;
  const appBottom = appY + APP_H;

  const appColor = typeMeta('Application').color;
  const depColor = typeMeta('Deployment').color;

  return (
    // Horizontal scroll lives on this container, never on the page: a wide
    // estate must not make the whole fact sheet scroll sideways.
    <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#FCFDFE' }}>
      <svg
        width={model.width}
        height={model.height}
        viewBox={`0 0 ${model.width} ${model.height}`}
        role="img"
        aria-label={`Deployment topology for ${appName}`}
        style={{ display: 'block' }}
      >
        {/* Application -> deployments. A single horizontal rail with a drop per
            column, rather than one diagonal each, so the fan-out stays readable
            when there are six environments. */}
        {model.placed.length > 0 && (
          <>
            {line(appAnchorX, appBottom, appAnchorX, appBottom + TIER_GAP / 2, 'app-drop')}
            {model.placed.length > 1 && line(
              railFrom, appBottom + TIER_GAP / 2, railTo, appBottom + TIER_GAP / 2, 'app-rail',
            )}
            {model.placed.map((c) => line(
              c.centre, appBottom + TIER_GAP / 2, c.centre, depY, `drop-${c.id}`,
            ))}
          </>
        )}

        <Node
          x={appX} y={appY} w={APP_W} h={APP_H}
          label={appName}
          sub={urls && (urls.publicUrl || urls.privateUrl)
            ? (urls.publicUrl || urls.privateUrl)
            : undefined}
          color={appColor}
          onClick={appId && onOpen ? () => onOpen(appId) : undefined}
        />

        {model.placed.map((c) => {
          const depX = c.depX;
          const cx = c.centre;
          // Servers are laid out in rows of SERVERS_PER_ROW, each row centred
          // under the deployment so the block reads as one group rather than
          // ragged.
          const rowWidth = (n) => n * SRV_W + (n - 1) * SRV_GAP;
          const srvPos = c.shown.map((s, i) => {
            const row = Math.floor(i / SERVERS_PER_ROW);
            const inRow = i % SERVERS_PER_ROW;
            const countThisRow = Math.min(SERVERS_PER_ROW, c.shown.length - row * SERVERS_PER_ROW);
            const rowLeft = cx - rowWidth(countThisRow) / 2;
            return { s, x: rowLeft + inRow * (SRV_W + SRV_GAP), y: srvY + row * (SRV_H + SRV_GAP), row };
          });
          const firstRow = srvPos.filter((p) => p.row === 0);
          return (
            <g key={c.id}>
              <Node
                x={depX} y={depY} w={DEP_W} h={DEP_H}
                label={c.name}
                sub={[c.stage, c.lifecycle].filter(Boolean).join(' · ') || undefined}
                color={depColor}
                onClick={onOpen ? () => onOpen(c.id) : undefined}
              />

              {/* Deployment -> its servers. The fan is the load balancer:
                  several hosts answering for one address. */}
              {!!c.shown.length && (
                <>
                  {line(cx, depY + DEP_H, cx, depY + DEP_H + TIER_GAP / 2, `sdrop-${c.id}`)}
                  {/* The rail spans the FIRST row only. Rows below hang from
                      the same drop, which is what makes a wrapped block still
                      read as one fan-out rather than two. */}
                  {firstRow.length > 1 && line(
                    firstRow[0].x + SRV_W / 2,
                    depY + DEP_H + TIER_GAP / 2,
                    firstRow[firstRow.length - 1].x + SRV_W / 2,
                    depY + DEP_H + TIER_GAP / 2,
                    `srail-${c.id}`,
                  )}
                  {firstRow.map((p) => line(
                    p.x + SRV_W / 2, depY + DEP_H + TIER_GAP / 2, p.x + SRV_W / 2, srvY, `sl-${p.s.id}`,
                  ))}
                  {srvPos.map((p) => (
                    <Node
                      key={p.s.id}
                      x={p.x} y={p.y} w={SRV_W} h={SRV_H}
                      label={p.s.name}
                      color={typeMeta(p.s.type || 'ITComponent').color}
                      onClick={onOpen ? () => onOpen(p.s.id) : undefined}
                    />
                  ))}
                  {c.hidden > 0 && (
                    <text
                      x={cx} textAnchor="middle"
                      y={srvY + model.serverBlockH + 12}
                      fontSize={10.5} fill="#5C6B7A"
                    >
                      {`+${c.hidden} more server${c.hidden === 1 ? '' : 's'} not drawn`}
                    </text>
                  )}
                </>
              )}

              {/* A deployment with no servers is DRAWN and labelled, not
                  omitted. An absent box reads as "nothing to see"; a box
                  saying "no servers linked" is a finding. */}
              {!c.shown.length && (
                <text x={cx} textAnchor="middle" y={depY + DEP_H + 16} fontSize={10.5} fill="#8A97A4">
                  no servers linked
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 0.75, color: 'text.secondary' }}>
        The address belongs to the deployment; the servers beneath it are the hosts answering for it.
        {model.placed.some((c) => c.hidden > 0)
          ? ` Server boxes are capped at ${SERVER_CAP} per deployment; the overflow is stated above.`
          : ''}
      </Typography>
    </Box>
  );
}
